import { Suggest, Currency, Cars } from './../../types';
import { GetterTree, MutationTree, ActionTree, ActionContext } from 'vuex';
import { SuggestService } from 'api';
import { Subscription, of, timer } from 'rxjs';
import { filter, mergeMap, tap, debounce, finalize } from 'rxjs/operators';
import { Events } from './../../analytics/events';

interface Subscriptions {
  id: Suggest.Ids;
  subscription?: Subscription;
}
const subscriptions: Subscriptions[] = [];

export class State {
  inputs: Suggest.SuggestContainer<Suggest.TypedSuggest>[] = [];
}

const getters = <GetterTree<State, any>>{
  getQuery: (state: State) => (id: Suggest.Ids) => {
    const inputContainer = state.inputs.find(it => it.inputId === id);
    if (inputContainer) {
      return inputContainer.query;
    } else {
      return null;
    }
  },

  getSuggest:
    <T extends Suggest.TypedSuggest>(state: State) =>
    (id: Suggest.Ids): Suggest.Suggest<T> | null => {
      const inputContainer = state.inputs.find(it => it.inputId === id);
      if (inputContainer) {
        return inputContainer.selected as Suggest.Suggest<T>;
      } else {
        return null;
      }
    },

  getSuggests:
    <T extends Suggest.TypedSuggest>(state: State) =>
    (id: Suggest.Ids): Suggest.Suggest<T>[] => {
      const inputContainer = state.inputs.find(it => it.inputId === id);
      if (inputContainer) {
        return inputContainer.suggests as Suggest.Suggest<T>[];
      } else {
        return null;
      }
    },

  suggestIsNotEmpty:
    (state: State) =>
    (id: Suggest.Ids): boolean => {
      const inputContainer = state.inputs.find(it => it.inputId === id);
      if (inputContainer) {
        return inputContainer.suggests.length > 0;
      } else {
        return false;
      }
    },

  queryIsEmpty:
    (state: State) =>
    (id: Suggest.Ids): boolean => {
      const inputContainer = state.inputs.find(it => it.inputId === id);
      if (inputContainer) {
        return inputContainer.query.text
          ? inputContainer.query.text.length === 0
          : true;
      } else {
        return false;
      }
    },

  loading:
    (state: State) =>
    (id: Suggest.Ids): boolean => {
      const inputContainer = state.inputs.find(it => it.inputId === id);
      if (inputContainer) {
        return inputContainer.loading;
      } else {
        return false;
      }
    },

  error:
    (state: State) =>
    (id: Suggest.Ids): string => {
      const inputContainer = state.inputs.find(it => it.inputId === id);
      if (inputContainer) {
        return inputContainer.error;
      } else {
        return '';
      }
    },
  inputInited:
    (state: State) =>
    (id: Suggest.Ids): boolean => {
      const inputContainer = state.inputs.find(it => it.inputId === id);
      if (inputContainer) {
        return true;
      } else {
        return false;
      }
    },
};

const mutations = <MutationTree<State>>{
  initSuggestContainer<T extends Suggest.TypedSuggest>(
    state: State,
    inputId: Suggest.Ids,
  ) {
    const alreadyInCollection = state.inputs.find(x => x.inputId === inputId);
    if (alreadyInCollection) {
      return;
    } else {
      state.inputs.push(new Suggest.SuggestContainer<T>({ id: inputId }));
    }
  },

  setSuggestContainerInstance<T extends Suggest.TypedSuggest>(
    state: State,
    input: Suggest.SuggestContainer<T>,
  ) {
    const inputContainers = state.inputs.map(it =>
      it.inputId !== input.inputId ? it : input,
    );
    state.inputs = inputContainers;
  },

  setSuggestsById<T extends Suggest.TypedSuggest>(
    state: State,
    { suggests, id }: { suggests: Suggest.Suggest<T>[]; id: Suggest.Ids },
  ) {
    const inputIndex = state.inputs.findIndex(x => x.inputId === id);
    if (inputIndex >= 0) {
      state.inputs[inputIndex].suggests = suggests;
    } else {
      state.inputs.push(
        new Suggest.SuggestContainer<T>({ id: id, suggests: suggests }),
      );
    }
  },

  setQueryByIndex<T extends Suggest.TypedSuggest>(
    state: State,
    { query, id }: { query: Suggest.Query; id: Suggest.Ids },
  ) {
    const inputIndex = state.inputs.findIndex(x => x.inputId === id);
    if (inputIndex >= 0) {
      state.inputs[inputIndex].query.text = decodeURIComponent(query.text);
      state.inputs[inputIndex].query.type = query.type;
    } else {
      state.inputs.push(
        new Suggest.SuggestContainer<T>({ id: id, query: query }),
      );
    }
  },

  onSelectSuggest<T extends Suggest.TypedSuggest>(
    state: State,
    { suggest, id }: { suggest: Suggest.Suggest<T>; id: Suggest.Ids },
  ) {
    const inputIndex = state.inputs.findIndex(x => x.inputId === id);
    if (inputIndex >= 0) {
      state.inputs[inputIndex].selected = suggest;
    } else {
      state.inputs.push(
        new Suggest.SuggestContainer<T>({ id: id, suggest: suggest }),
      );
    }

    if (id === Suggest.Ids.HOTEL && suggest) {
      const source = suggest.source as Suggest.HotelSuggest;
      if (!source.geo) {
        return;
      }

      Events.Hotel.Suggest.select({
        title: suggest.title,
        subtitle: suggest.subtitle,
        country_code: source?.geo?.country,
        type: source?.type,
      });
    }
  },

  removeInput(state: State, inputId: Suggest.Ids) {
    const inputIndex = state.inputs.findIndex(x => x.inputId === inputId);
    if (inputIndex >= 0) {
      state.inputs.splice(inputIndex, 1);
    }
  },

  setLoading(
    state: State,
    { loading, id }: { loading: boolean; id: Suggest.Ids },
  ) {
    const inputIndex = state.inputs.findIndex(x => x.inputId === id);
    if (inputIndex >= 0) {
      state.inputs[inputIndex].loading = loading;
    } else {
      state.inputs.push(
        new Suggest.SuggestContainer<Suggest.TypedSuggest>({
          id: id,
          loading: loading,
        }),
      );
    }
  },

  clear(state: State, id: Suggest.Ids) {
    const inputIndex = state.inputs.findIndex(x => x.inputId === id);
    if (inputIndex >= 0) {
      state.inputs[inputIndex].clear();
    }
  },
  reset(state: State) {
    state.inputs.forEach(it => it.reset());
  },

  resetById(state: State, id: Suggest.Ids) {
    const inputIndex = state.inputs.findIndex(x => x.inputId === id);
    if (inputIndex >= 0) {
      state.inputs[inputIndex].reset();
    }
  },

  setError(state: State, { error, id }: { error: string; id: Suggest.Ids }) {
    const inputIndex = state.inputs.findIndex(x => x.inputId === id);
    if (inputIndex >= 0 && error) {
      state.inputs[inputIndex].error = error;
    }
  },
};

const actions = <ActionTree<State, any>>{
  async search<T>(
    store: ActionContext<State, any>,
    {
      query,
      id,
      type,
    }: { query: Suggest.Query; id: Suggest.Ids; type: Suggest.Type },
  ) {
    let subscriptionId = subscriptions.findIndex(it => it.id === id);

    if (subscriptionId >= 0) {
      subscriptions[subscriptionId].subscription.unsubscribe();
    } else {
      subscriptions.push({
        id: id,
        subscription: null,
      });

      subscriptionId = subscriptions.findIndex(it => it.id === id);
    }

    store.commit('setQueryByIndex', { query: query, id: id });
    const inputContainer = store.state.inputs.find(it => it.inputId === id);

    if (inputContainer.query.text.length < 3) {
      store.commit('setSuggestsById', {
        suggests: [],
        id: id,
      });
    }

    store.commit('setError', {
      id: id,
      error: '',
    });

    const startTime = new Date().getTime();
    const CURRENCY = store.rootState.support.currency as Currency.CurrencyItem;

    const requestBody = () => {
      switch (type) {
        case Suggest.Type.CAR: {
          return {
            q: query.text,
          };
        }

        case Suggest.Type.FLIGHT: {
          return query.text;
        }

        default: {
          return query.text;
        }
      }
    };

    subscriptions[subscriptionId].subscription = of(query.text)
      .pipe(
        filter(it => it.trimLeft().length >= 3),
        debounce(() => timer(type === Suggest.Type.CAR ? 0 : 600)),
        tap(() => {
          store.commit('setLoading', { loading: true, id: id });
        }),
        tap(it => {
          if (
            query.type !== Suggest.QueryType.AUTO &&
            type === Suggest.Type.HOTEL
          ) {
            Events.Hotel.Suggest.start(it);
          }
        }),
        mergeMap(it => SuggestService.fetchSuggests(requestBody(), type)),
      )
      .pipe(
        finalize(() => {
          const inputContainer = store.state.inputs.find(
            it => it.inputId === id,
          );
          if (
            inputContainer &&
            inputContainer.query.text === query.text &&
            inputContainer
          ) {
            store.commit('setLoading', { loading: false, id });
          }
        }),
      )
      .subscribe(
        async suggests => {
          store.commit('setSuggestsById', { suggests, id });
          const inputIndex = store.state.inputs.findIndex(
            x => x.inputId === id,
          );
          const selected = store.state.inputs[inputIndex].selected;

          if (inputIndex >= 0 && selected && selected.source.id) {
            const selectedFullModel = suggests.find(
              it => it.source.id === selected.source.id,
            );

            if (type === Suggest.Type.FLIGHT) {
              if (selectedFullModel) {
                store.commit('onSelectSuggest', {
                  suggest: selectedFullModel,
                  id: id,
                });
              }
            } else if (type === Suggest.Type.HOTEL) {
              const asHotelSuggest = selected.source as Suggest.HotelSuggest;

              if (selectedFullModel && !asHotelSuggest.geo) {
                store.commit('onSelectSuggest', {
                  suggest: selectedFullModel,
                  id: id,
                });
              }
            } else if (type === Suggest.Type.CAR) {
              const carsCriteria = store.rootState.car
                .searchCriteria as Cars.SearchCriteria;
              const iata =
                id === Suggest.Ids.CAR_PICK
                  ? carsCriteria.pickUpId
                  : carsCriteria.dropOffId;

              if (!iata) {
                return;
              }
              const matchedByIATA = suggests.find(
                it => (it.source as Suggest.CarsSuggest).iata === iata,
              );
              if (matchedByIATA) {
                store.commit('onSelectSuggest', {
                  suggest: matchedByIATA,
                  id: id,
                });
              }
            }
          }

          if (
            query.type !== Suggest.QueryType.AUTO &&
            type === Suggest.Type.HOTEL
          ) {
            Events.Hotel.Suggest.finish({
              startTimestamp: startTime,
              total: suggests.length,
              duration: (new Date().getTime() - startTime).millisecondsToSec(),
              text: query.text,
            });
          }
        },
        err => {
          store.commit('setError', {
            id: id,
            error: err && err.message ? err.message : '',
          });
        },
      );
  },

  setSuggest<T extends Suggest.TypedSuggest>(
    store: ActionContext<State, any>,
    { suggest, id }: { suggest: Suggest.Suggest<T>; id: Suggest.Ids },
  ) {
    store.commit('onSelectSuggest', { suggest: suggest, id: id });
  },
};

export const suggest = {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions,
};

export default {};
